/**
 * Chat API service for the AI Chat module
 */
import axiosInstance from '../../../shared/api/axiosInstance';
import type { ResolvedEntity } from '../../../shared/types';

// Re-export for convenience
export type { ResolvedEntity };

export interface ChatSessionToolCall {
  id: string;
  tool_name: string;
  module: string;
  status: 'running' | 'success' | 'error';
  latency_ms?: number | null;
  input_data?: Record<string, unknown>;
  output_data?: Record<string, unknown>;
  error_message?: string | null;
  call_order: number;
  created_at?: string;
}

export interface ChatSession {
  id: string;
  chat_type: 'assistant' | 'agent' | 'avatar';
  status: 'active' | 'ended' | 'archived';
  title: string | null;
  context: Record<string, unknown>;
  about_entities: string[];
  about_entities_resolved: ResolvedEntity[];
  enabled_modules: string[];
  model_category: string | null;
  /** Per-session reasoning-effort override; null means inherit. */
  reasoning_effort: string | null;
  effective_model_id: string | null;
  accepted_mime_types?: string[];
  messages: ChatSessionMessage[];
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatSessionMessage {
  id: string;
  chat_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  /** Canonical monotonic per-chat sequence number (see ChatMessage.seq). */
  seq?: number;
  timestamp: string;
  tool_calls?: ChatSessionToolCall[];
  attachment_file_ids?: string[];
  is_compaction_summary?: boolean;
  interrupted?: boolean;
}

export interface ChatSearchResult {
  id: string;
  title: string | null;
  chat_type: string;
  status: string;
  message_count: number;
  snippet: string | null;
  created_at: string;
  updated_at: string;
}

export interface SystemPromptSectionChild {
  key: string;
  label: string;
  description?: string;
  item_type?: string;
  loading_mode?: string;
  access_level?: string;
  source?: string;
  external_id?: string;
  popup_content?: string;
  char_count?: number;
  estimated_tokens?: number;
}

export interface SystemPromptSection {
  key: string;
  label: string;
  content: string;
  category?: string;
  char_count?: number;
  estimated_tokens?: number;
  children?: SystemPromptSectionChild[];
}

export interface DebugToolDetail {
  name: string;
  module: string;
  description: string;
  param_count: number;
  required_params: string[];
  schema_chars: number;
  estimated_tokens: number;
}

export interface DebugTotals {
  prompt_chars: number;
  prompt_estimated_tokens: number;
  tool_count: number;
  tool_schema_chars: number;
  tool_estimated_tokens: number;
  total_estimated_tokens: number;
}

export interface ContextTelemetryRound {
  round: number;
  message_count: number;
  context_chars: number;
  system_chars: number;
  est_context_tokens: number;
}

export interface SystemPromptDebugInfo {
  sections: SystemPromptSection[];
  tools: DebugToolDetail[];
  totals: DebugTotals;
  model_category: string | null;
  enabled_modules: string[];
}

export interface SystemPromptDebugResponse {
  sections: SystemPromptSection[];
  debug: SystemPromptDebugInfo | null;
  context_telemetry: ContextTelemetryRound[] | null;
  context_window_tokens: number | null;
}

export interface UpdateSessionRequest {
  /** Explicit null clears the per-session override; omit the field for "no change". */
  model_category?: string | null;
  /** Explicit null clears the per-session override; omit the field for "no change". */
  reasoning_effort?: string | null;
  title?: string;
  enabled_modules?: string[];
  enabled_tools?: string[];
  disabled_tools?: string[];
  module_access?: Record<string, string>;
  tool_scopes?: Record<string, string[]>;
  web_search?: boolean;
  web_fetch?: boolean;
  web_navigate?: boolean;
}

export interface CreateSessionRequest {
  chat_type?: 'assistant' | 'agent' | 'avatar';
  context?: Record<string, unknown>;
  about_entities?: string[];
  title?: string;
  /** Override the default AI model category for this session at creation time. */
  model_category?: string | null;
  /** Override the reasoning effort for this session at creation time. */
  reasoning_effort?: string | null;
}

export interface SendMessageRequest {
  message: string;
  stream?: boolean;
}

export interface SendMessageResponse {
  response: string;
  message_id?: string;
}

// Prompt configuration types (reused from agents)
export interface PromptPartPreset {
  id: string;
  name: string;
  content: string;
  sort_order: number;
}

export interface PromptPart {
  id: string;
  name: string;
  description: string | null;
  default_content: string | null;
  presets: PromptPartPreset[];
}

export interface PromptPartConfig {
  preset_id?: string;
  custom?: string;
}

export interface PromptConfig {
  [partId: string]: PromptPartConfig;
}

export interface PromptConfigResponse {
  prompt_config: PromptConfig;
  available_parts: PromptPart[];
}

export const chatApi = {
  /** List chat sessions for the current user, filtered by type */
  async listSessions(chatType?: 'assistant' | 'agent' | 'avatar'): Promise<{ data: ChatSession[] }> {
    const params = chatType ? { chat_type: chatType } : {};
    const response = await axiosInstance.get('/api/v1/ai-chat/sessions', { params });
    return response.data;
  },

  /** Create a new chat session */
  async createSession(request: CreateSessionRequest = {}): Promise<{ data: ChatSession }> {
    const response = await axiosInstance.post('/api/v1/ai-chat/sessions', {
      chat_type: request.chat_type || 'assistant',
      context: request.context || {},
      about_entities: request.about_entities || [],
      title: request.title,
      ...(request.model_category ? { model_category: request.model_category } : {}),
      ...(request.reasoning_effort ? { reasoning_effort: request.reasoning_effort } : {}),
    });
    return response.data;
  },

  /** Get a chat session by ID with full message history */
  async getSession(sessionId: string): Promise<{ data: ChatSession }> {
    const response = await axiosInstance.get(`/api/v1/ai-chat/sessions/${sessionId}`);
    return response.data;
  },

  /** Get chat sessions linked to a specific entity (backlinks) */
  async getSessionsByEntity(entityRef: string): Promise<{ data: ChatSession[] }> {
    const response = await axiosInstance.get(`/api/v1/ai-chat/sessions/by-entity/${entityRef}`);
    return response.data;
  },

  /** Send a message to a chat session */
  async sendMessage(sessionId: string, message: string): Promise<{ data: SendMessageResponse }> {
    const response = await axiosInstance.post(
      `/api/v1/ai-chat/sessions/${sessionId}/messages`,
      { message, stream: false }
    );
    return response.data;
  },

  /** Permanently delete a chat session */
  async deleteSession(sessionId: string): Promise<void> {
    await axiosInstance.delete(`/api/v1/ai-chat/sessions/${sessionId}`);
  },

  /** Archive a chat session */
  async archiveSession(sessionId: string): Promise<{ data: ChatSession }> {
    const response = await axiosInstance.post(`/api/v1/ai-chat/sessions/${sessionId}/archive`);
    return response.data;
  },

  /** Restore an archived chat session */
  async unarchiveSession(sessionId: string): Promise<{ data: ChatSession }> {
    const response = await axiosInstance.post(`/api/v1/ai-chat/sessions/${sessionId}/unarchive`);
    return response.data;
  },

  /** Remove an entity link from a chat session */
  async removeEntityFromSession(sessionId: string, entityRef: string): Promise<{ data: ChatSession }> {
    const response = await axiosInstance.delete(
      `/api/v1/ai-chat/sessions/${sessionId}/entities/${entityRef}`
    );
    return response.data;
  },

  /** Update session (title, model, MCP config) */
  async updateSession(sessionId: string, data: UpdateSessionRequest): Promise<{ data: ChatSession }> {
    const response = await axiosInstance.patch(`/api/v1/ai-chat/sessions/${sessionId}`, data);
    return response.data;
  },

  /** Update session title (convenience wrapper) */
  async updateSessionTitle(sessionId: string, title: string): Promise<{ data: ChatSession }> {
    return this.updateSession(sessionId, { title });
  },

  /** Search across chat sessions */
  async searchSessions(query: string, chatType?: string): Promise<{ data: ChatSearchResult[] }> {
    const params: Record<string, string> = { q: query };
    if (chatType) params.chat_type = chatType;
    const response = await axiosInstance.get('/api/v1/ai-chat/sessions/search', { params });
    return response.data;
  },

  /** Get system prompt sections and debug info for a chat session */
  async getSystemPrompt(sessionId: string): Promise<{ data: SystemPromptDebugResponse }> {
    const response = await axiosInstance.get(`/api/v1/ai-chat/sessions/${sessionId}/system-prompt`);
    return response.data;
  },

  // ============== User Settings ==============

  /** Get user's prompt config with available parts and presets */
  async getPromptConfig(): Promise<{ data: PromptConfigResponse }> {
    const response = await axiosInstance.get('/api/v1/ai-chat/settings/user/prompt-config');
    return response.data;
  },

  /** Update user's prompt config */
  async updatePromptConfig(promptConfig: PromptConfig): Promise<{ data: { prompt_config: PromptConfig } }> {
    const response = await axiosInstance.put('/api/v1/ai-chat/settings/user/prompt-config', {
      prompt_config: promptConfig,
    });
    return response.data;
  },
};
